import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import Icon from '../components/Icon';
import GuideContent, { assetUrl } from '../components/GuideContent';
import api from '../lib/api';
import { logError } from '../lib/log';
import { useAuth } from '../context/AuthContext';
import { usePageMeta } from '../hooks/usePageMeta';
import { useReading } from '../hooks/useReading';
import { CHALLENGES } from '../lib/assessment';
import { ghostBtn, primaryBtn } from '../components/dashboard/ui';

const SITE_URL = (process.env.REACT_APP_SITE_URL || '').replace(/\/$/, '');

function TopicChip({ t, read }) {
  return (
    <Link
      to={`/topics/${t.slug}`}
      data-testid={`related-topic-${t.canonical_id}`}
      className="p-space-md bg-surface rounded-lg hover:bg-primary-container transition-colors group flex items-start justify-between shadow-sm"
    >
      <div className="pr-space-sm">
        <div className="flex items-center gap-space-xs mb-1">
          <span className="font-label-sm text-label-sm uppercase tracking-wider text-surface-tint font-bold">{t.domain}</span>
        </div>
        <h4 className="font-title-md text-title-md text-outline font-bold group-hover:text-surface-tint transition-colors">{t.display_title || t.title}</h4>
        {read ? <span className="font-label-sm text-label-sm uppercase tracking-wider text-surface-tint font-bold mt-1 block" data-testid={`read-flag-${t.slug}`}>✓ Read</span> : null}
      </div>
      <Icon name="chevron_right" className="text-outline group-hover:text-surface-tint group-hover:translate-x-1 transition-all" />
    </Link>
  );
}

export default function TopicDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const isAuthed = user && typeof user === 'object';
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ok | notfound | error
  const [saved, setSaved] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [savingBusy, setSavingBusy] = useState(false);
  const [addedHabits, setAddedHabits] = useState({}); // habit text -> true
  const [notice, setNotice] = useState('');
  const { readSlugs, mark } = useReading();

  const metaTitle = data?.topic?.seo_title
    || (data?.topic?.title ? `${data.topic.title} | OVERALL WELLBEING` : 'Wellbeing Topic | OVERALL WELLBEING');
  const metaDescription = data?.topic?.meta_description || (data
    ? (data.content?.summary
        ? String(data.content.summary).replace(/\s+/g, ' ').slice(0, 300)
        : `A clear, practical guide to ${data.topic.title}${data.domain ? ` within ${data.domain.name}` : ''} — part of the OVERALL WELLBEING library.`)
    : 'A clear, practical wellbeing guide from the OVERALL WELLBEING library.');
  usePageMeta({
    title: metaTitle,
    description: metaDescription,
    path: `/topics/${slug}`,
    image: data?.topic?.share_image ? assetUrl(data.topic.share_image) : undefined,
    noindex: status === 'notfound' || data?.topic?.seo_index !== true,
  });

  useEffect(() => {
    let active = true;
    setStatus('loading');
    (async () => {
      try {
        const { data } = await api.get(`/knowledge/topics/${slug}/detail`);
        if (!active) return;
        setData(data);
        setStatus('ok');
      } catch (e) {
        if (!active) return;
        logError('TopicDetail.load', e);
        setStatus(e.response?.status === 404 ? 'notfound' : 'error');
      }
    })();
    return () => { active = false; };
  }, [slug]);

  // Personal state: record exploration + load saved/habit state for this topic.
  useEffect(() => {
    if (status !== 'ok' || !isAuthed || !data) return;
    const cid = data.topic.canonical_id;
    let active = true;
    (async () => {
      api.post(`/me/topic-view/${cid}`).catch(() => {});
      try {
        const [sv, hb] = await Promise.all([api.get('/me/saved'), api.get('/me/habits')]);
        if (!active) return;
        const match = (sv.data || []).find((s) => s.content_type === 'topic' && s.content_id === cid);
        setSaved(!!match);
        setSavedId(match ? match.id : null);
        const added = {};
        (hb.data || []).forEach((h) => { if (h.source_topic_id === cid && h.title) added[h.title] = true; });
        setAddedHabits(added);
      } catch (e) { logError('TopicDetail.personalState', e); }
    })();
    return () => { active = false; };
  }, [status, isAuthed, data]);

  const toggleSave = async () => {
    if (!data) return;
    setSavingBusy(true);
    setNotice('');
    try {
      const t = data.topic;
      if (saved && savedId) {
        await api.delete(`/me/saved/${savedId}`);
        setSaved(false); setSavedId(null);
      } else {
        const { data: doc } = await api.post('/me/saved', {
          content_type: 'topic', content_id: t.canonical_id,
          title: t.title, slug: t.slug, domain: t.domain,
        });
        setSaved(true); setSavedId(doc.id);
      }
    } catch (e) {
      logError('TopicDetail.toggleSave', e);
      setNotice('Something went wrong. Please try again.');
    } finally { setSavingBusy(false); }
  };

  // Reading progress: the end-of-guide sentinel marks it read automatically.
  const endRef = useRef(null);
  const slug_read = data?.topic?.slug;
  const alreadyRead = slug_read ? readSlugs.includes(slug_read) : false;
  const markRead = useCallback(async () => {
    if (!slug_read) return;
    try {
      await mark(slug_read);
    } catch (e) {
      logError('TopicDetail.markRead', e);
      setNotice('Something went wrong. Please try again.');
    }
  }, [slug_read, mark]);

  useEffect(() => {
    if (!isAuthed || !slug_read || alreadyRead || !endRef.current) return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) markRead();
    });
    observer.observe(endRef.current);
    return () => observer.disconnect();
  }, [isAuthed, slug_read, alreadyRead, markRead]);

  const addHabit = async (text) => {
    if (!data) return;
    const t = data.topic;
    const fcode = (data.foundations && data.foundations[0] && data.foundations[0].code) || null;
    setNotice('');
    try {
      await api.post('/me/habits', {
        title: text, source_topic_id: t.canonical_id,
        source_topic_slug: t.slug, source_topic_title: t.display_title || t.title, foundation_code: fcode,
      });
      setAddedHabits((m) => ({ ...m, [text]: true }));
    } catch (e) {
      logError('TopicDetail.addHabit', e);
      setNotice('Something went wrong. Please try again.');
    }
  };

  if (status === 'loading') {
    return (
      <div className="w-full flex items-center justify-center py-space-3xl" data-testid="topic-loading">
        <span className="inline-block w-2 h-2 rounded-full bg-surface-tint animate-pulse" />
        <span className="ml-space-sm font-label-md text-label-md uppercase tracking-wider text-on-surface-variant">Opening topic…</span>
      </div>
    );
  }

  if (status === 'notfound' || status === 'error') {
    return (
      <div className="w-full px-margin-mobile lg:px-margin py-space-3xl" data-testid="topic-notfound">
        <div className="max-w-2xl mx-auto text-center">
          <h1 className="font-headline-md text-headline-md uppercase text-outline tracking-tight mb-space-sm">Topic not found</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mb-space-lg">We couldn’t find that topic. Browse the full library to find what you’re looking for.</p>
          <Link to="/resources" className="inline-flex items-center gap-space-xs h-12 px-space-xl rounded-lg bg-surface-tint text-on-primary font-label-md text-label-md uppercase tracking-wider hover:bg-primary transition-all">
            <Icon name="menu_book" className="text-[18px]" /> Back to the Library
          </Link>
        </div>
      </div>
    );
  }

  const { topic, content, domain, foundations, siblings, related, availability } = data;
  const curatedRelated = data.curated_related || [];
  if (topic.redirect_to) return <Navigate to={topic.redirect_to} replace />;
  const hasFoundations = foundations && foundations.length > 0;
  const hasContent = content && (content.summary || (content.actions || []).length || (content.habits || []).length);
  // An imported guide carries its own editorial content, so the older
  // auto-generated summary, actions and habits blocks are hidden for it.
  const isGuide = (topic.sections || []).length > 0;
  const isRead = readSlugs.includes(topic.slug);
  const guideChallenge = CHALLENGES.find((c) => (c.guides || []).includes(topic.slug)) || null;
  const relatedCount = curatedRelated.length || related.length;

  return (
    <div className="flex flex-col w-full" data-testid="topic-detail">
      {/* Breadcrumbs */}
      <section className="w-full px-margin-mobile lg:px-margin pt-space-xl pb-space-lg">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-space-md">
          <div className="flex items-center flex-wrap gap-space-xs text-on-surface-variant font-label-md text-label-md uppercase tracking-wider">
            <Link to="/wellbeing" className="hover:text-surface-tint transition-colors">EXPLORE WELLBEING</Link>
            <span className="text-surface-container-highest">/</span>
            <Link to="/resources" className="hover:text-surface-tint transition-colors">{domain ? domain.name : 'LIBRARY'}</Link>
            <span className="text-surface-container-highest">/</span>
            <span className="text-outline font-extrabold" data-testid="topic-canonical-id">{topic.display_title || topic.title}</span>
          </div>
          <Link to="/resources" className="inline-flex items-center gap-space-xs font-label-md text-label-md uppercase tracking-wider text-surface-tint font-bold hover:text-primary transition-colors">
            <Icon name="arrow_back" className="text-[18px]" /> Back to Library
          </Link>
        </div>
      </section>

      {/* HERO */}
      <section className="w-full px-margin-mobile lg:px-margin pb-space-2xl bg-surface relative overflow-hidden">
        <div className="absolute top-0 right-1/4 w-[420px] h-[420px] rounded-full bg-primary-container/50 blur-[120px] pointer-events-none -z-10" />
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap items-center gap-space-sm mb-space-md">
            <span className="w-2 h-2 rounded-full bg-surface-tint shadow-[0_0_8px_rgba(240,40,223,0.7)]" />
            <span className="font-label-md text-label-md uppercase tracking-widest text-surface-tint font-bold">{domain ? domain.name : 'WELLBEING GUIDE'}</span>
          </div>
          <h1 data-testid="topic-title" className="font-headline-lg text-headline-lg lg:text-display-hero lg:font-display-hero uppercase text-outline tracking-tight leading-tight lg:leading-none mb-space-lg">
            {topic.display_title || topic.title}
          </h1>
          {isGuide ? null : hasContent && content.summary ? (
            <p data-testid="topic-summary" className="font-body-lg text-body-lg text-on-surface-variant max-w-3xl leading-relaxed mb-space-xl">
              {content.summary}
            </p>
          ) : (
            <p data-testid="topic-summary" className="font-body-lg text-body-lg text-on-surface-variant max-w-3xl leading-relaxed mb-space-xl">
              <span className="font-semibold text-outline">{topic.title}</span> is one of the guides in the OVERALL WELLBEING library. It sits within {domain ? domain.name : 'wellbeing'}{hasFoundations ? ` and is part of ${foundations.length === 1 ? 'one of the Essentials' : `${foundations.length} of the Essentials`}` : ''}. Use the links below to explore how it connects to the rest of your wellbeing.
            </p>
          )}
          <div className="flex flex-wrap items-center gap-space-md mb-space-lg" data-testid="topic-save-row">
            {isAuthed ? (
              <button
                type="button"
                data-testid="topic-save-button"
                onClick={toggleSave}
                disabled={savingBusy}
                className={`h-11 px-space-lg rounded-lg font-label-md text-label-md uppercase tracking-wider font-semibold transition-all flex items-center gap-space-xs disabled:opacity-60 ${saved ? 'bg-outline text-on-primary' : 'bg-surface-tint text-on-primary hover:bg-primary shadow-[0_4px_16px_rgba(240,40,223,0.3)]'}`}
              >
                <Icon name={saved ? 'bookmark' : 'bookmark_border'} className="text-[18px]" />
                <span>{saved ? 'Saved to your library' : 'Save this topic'}</span>
              </button>
            ) : (
              <Link to="/my-login" data-testid="topic-save-signin" className="h-11 px-space-lg rounded-lg bg-surface-container text-outline hover:text-surface-tint font-label-md text-label-md uppercase tracking-wider font-semibold transition-all flex items-center gap-space-xs">
                <Icon name="bookmark_border" className="text-[18px]" />
                <span>Sign in to save</span>
              </Link>
            )}
          </div>
          {notice ? (
            <p className="font-body-md text-body-md text-on-surface-variant mb-space-lg" data-testid="topic-notice">{notice}</p>
          ) : null}

          <div className="w-full p-space-md bg-surface-container rounded-lg flex flex-wrap items-center gap-space-lg">
            <div className="flex items-center gap-space-sm"><Icon name="category" className="text-[16px] text-surface-tint" /><span className="font-label-md text-label-md uppercase tracking-wider text-outline">{domain ? domain.name : '—'}</span></div>
            {foundations.length > 0 && (
              <>
                <span className="hidden sm:inline text-surface-container-highest">•</span>
                <div className="flex items-center gap-space-sm"><Icon name="star" className="text-[16px] text-surface-tint" /><span className="font-label-md text-label-md uppercase tracking-wider text-outline">{foundations.length} Essential{foundations.length === 1 ? '' : 's'}</span></div>
              </>
            )}
            {relatedCount > 0 && (
              <>
                <span className="hidden sm:inline text-surface-container-highest">•</span>
                <div className="flex items-center gap-space-sm"><Icon name="hub" className="text-[16px] text-surface-tint" /><span className="font-label-md text-label-md uppercase tracking-wider text-outline">{relatedCount} Related</span></div>
              </>
            )}
          </div>
        </div>
      </section>

      <GuideContent topic={topic} siteUrl={SITE_URL} />

      {isGuide ? (
        <section className="w-full px-margin-mobile lg:px-margin pb-space-2xl bg-surface-container-low" data-testid="guide-footer-actions">
          <div className="max-w-4xl mx-auto flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-space-md">
            {isAuthed ? (
              <button
                type="button"
                onClick={markRead}
                disabled={isRead}
                className={isRead ? ghostBtn : primaryBtn}
                data-testid="mark-as-read"
              >
                <Icon name={isRead ? 'check_circle' : 'check'} className="text-[18px]" />
                <span>{isRead ? '✓ Read' : 'Mark as read'}</span>
              </button>
            ) : null}
            {guideChallenge ? (
              <Link to="/my-login" className={ghostBtn} data-testid="try-challenge">
                <Icon name="flag" className="text-[18px]" />
                <span>Try the 7-day challenge</span>
              </Link>
            ) : null}
          </div>
        </section>
      ) : null}
      <div ref={endRef} />


      {/* FOUNDATIONS + DOMAIN */}
      <section className="w-full px-margin-mobile lg:px-margin py-space-2xl bg-surface-container-low">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-gutter">
          {/* Essentials */}
          {hasFoundations && (
            <div className="lg:col-span-7 bg-surface p-space-xl rounded-lg shadow-sm">
              <h2 className="font-headline-sm text-headline-sm uppercase text-outline mb-space-md">Part of these Essentials</h2>
              <div className="flex flex-col gap-space-sm" data-testid="topic-foundations">
                {foundations.map((f) => (
                  <Link key={f.code} to={`/essentials/${f.slug}`} className="flex items-center justify-between p-space-md rounded-lg bg-primary-container hover:bg-surface-container transition-colors group">
                    <div className="flex items-center gap-space-md">
                      <span className="w-9 h-9 rounded-full bg-surface-tint text-on-primary flex items-center justify-center font-label-md text-label-md font-bold">{String(f.order).padStart(2, '0')}</span>
                      <span className="font-title-md text-title-md text-outline font-bold group-hover:text-surface-tint transition-colors">{f.name}</span>
                    </div>
                    <Icon name="arrow_forward" className="text-outline group-hover:text-surface-tint transition-colors" />
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Domain */}
          <div className="lg:col-span-5 bg-outline text-surface p-space-xl rounded-lg shadow-md relative overflow-hidden">
            <div className="absolute -right-8 -bottom-8 w-40 h-40 rounded-full bg-surface-tint/20 blur-2xl pointer-events-none" />
            <span className="font-label-md text-label-md uppercase tracking-widest text-surface-tint font-bold block mb-space-xs">AREA OF LIFE</span>
            {domain ? (
              <>
                <h3 className="font-headline-sm text-headline-sm uppercase text-surface mb-space-sm">{domain.name}</h3>
                <p className="font-body-md text-body-md text-surface-container-high mb-space-lg">{domain.topic_count} guides in this area of life.</p>
                <Link to={domain.slug ? `/areas/${domain.slug}` : '/resources'} data-testid="topic-area-link" className="inline-flex items-center gap-space-xs font-label-md text-label-md uppercase tracking-wider text-surface-tint font-bold hover:text-primary-fixed-dim transition-colors">
                  <span>Browse this area of life</span>
                  <Icon name="arrow_forward" className="text-[18px]" />
                </Link>
              </>
            ) : (
              <p className="font-body-md text-body-md text-surface-container-high">Area of life information unavailable.</p>
            )}
          </div>
        </div>
      </section>

      {/* RECOMMENDED ACTIONS & HABITS */}
      {!isGuide && hasContent && ((content.actions || []).length > 0 || (content.habits || []).length > 0) && (
        <section className="w-full px-margin-mobile lg:px-margin py-space-2xl bg-surface" data-testid="topic-guidance">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-gutter">
            {(content.actions || []).length > 0 && (
              <div className="bg-surface-container-low p-space-xl rounded-lg shadow-sm" data-testid="topic-actions">
                <div className="flex items-center gap-space-sm mb-space-lg">
                  <span className="w-10 h-10 rounded-full bg-primary-container text-surface-tint flex items-center justify-center"><Icon name="bolt" className="text-[22px]" /></span>
                  <div>
                    <span className="font-label-md text-label-md uppercase tracking-widest text-surface-tint font-bold block">PRACTICAL STEPS</span>
                    <h2 className="font-headline-sm text-headline-sm uppercase text-outline">Recommended Actions</h2>
                  </div>
                </div>
                <ul className="flex flex-col gap-space-sm">
                  {content.actions.map((a, i) => (
                    <li key={i} className="flex items-start gap-space-sm p-space-md bg-surface rounded-lg">
                      <Icon name="check_circle" className="text-surface-tint text-[20px] mt-0.5 shrink-0" />
                      <span className="font-body-md text-body-md text-on-surface leading-relaxed">{a}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {(content.habits || []).length > 0 && (
              <div className="bg-surface-container-low p-space-xl rounded-lg shadow-sm" data-testid="topic-habits">
                <div className="flex items-center gap-space-sm mb-space-lg">
                  <span className="w-10 h-10 rounded-full bg-primary-container text-surface-tint flex items-center justify-center"><Icon name="autorenew" className="text-[22px]" /></span>
                  <div>
                    <span className="font-label-md text-label-md uppercase tracking-widest text-surface-tint font-bold block">EVERYDAY RHYTHM</span>
                    <h2 className="font-headline-sm text-headline-sm uppercase text-outline">Recommended Habits</h2>
                  </div>
                </div>
                <ul className="flex flex-col gap-space-sm">
                  {content.habits.map((h, i) => (
                    <li key={i} className="flex items-start gap-space-sm p-space-md bg-surface rounded-lg">
                      <Icon name="radio_button_checked" className="text-surface-tint text-[20px] mt-0.5 shrink-0" />
                      <span className="font-body-md text-body-md text-on-surface leading-relaxed flex-1">{h}</span>
                      {isAuthed && (
                        <button
                          type="button"
                          data-testid={`add-habit-${i}`}
                          onClick={() => addHabit(h)}
                          disabled={!!addedHabits[h]}
                          className={`shrink-0 inline-flex items-center gap-1 font-label-sm text-label-sm uppercase tracking-wider font-bold transition-colors ${addedHabits[h] ? 'text-secondary' : 'text-surface-tint hover:text-primary'}`}
                        >
                          <Icon name={addedHabits[h] ? 'check' : 'add_circle'} className="text-[16px]" />
                          <span>{addedHabits[h] ? 'Added' : 'Add'}</span>
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div className="max-w-7xl mx-auto mt-space-md">
            <p className="font-body-sm text-body-sm text-on-surface-variant italic">General guidance for awareness and healthy habits — not personal medical advice. If you have a health condition, check with a qualified professional.</p>
          </div>
        </section>
      )}

      {/* SIBLINGS + RELATED */}
      <section className="w-full px-margin-mobile lg:px-margin py-space-2xl bg-surface">
        <div className="max-w-7xl mx-auto flex flex-col gap-space-2xl">
          {curatedRelated.length > 0 && (
            <div>
              <div className="flex items-end justify-between mb-space-lg pb-space-md border-b border-surface-container">
                <div>
                  <span className="font-label-md text-label-md uppercase tracking-widest text-surface-tint font-bold block mb-space-xs">KEEP READING</span>
                  <h2 className="font-headline-sm text-headline-sm uppercase text-outline">Related guides</h2>
                </div>
                <span className="font-label-sm text-label-sm uppercase tracking-wider text-on-surface-variant">{curatedRelated.length} guide{curatedRelated.length === 1 ? '' : 's'}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-space-md" data-testid="topic-curated-related">
                {curatedRelated.map((t) => (<TopicChip key={t.canonical_id} t={t} read={readSlugs.includes(t.slug)} />))}
              </div>
            </div>
          )}

          {siblings.length > 0 && (
            <div>
              <div className="flex items-end justify-between mb-space-lg pb-space-md border-b border-surface-container">
                <div>
                  <span className="font-label-md text-label-md uppercase tracking-widest text-surface-tint font-bold block mb-space-xs">SAME AREA OF LIFE</span>
                  <h2 className="font-headline-sm text-headline-sm uppercase text-outline">In the same area of life</h2>
                </div>
                <span className="font-label-sm text-label-sm uppercase tracking-wider text-on-surface-variant">{domain ? domain.name : ''}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-space-md" data-testid="topic-siblings">
                {siblings.map((t) => (<TopicChip key={t.canonical_id} t={t} read={readSlugs.includes(t.slug)} />))}
              </div>
            </div>
          )}

          {related.length > 0 && curatedRelated.length === 0 && (
            <div>
              <div className="flex items-end justify-between mb-space-lg pb-space-md border-b border-surface-container">
                <div>
                  <span className="font-label-md text-label-md uppercase tracking-widest text-surface-tint font-bold block mb-space-xs">SHARED ESSENTIAL</span>
                  <h2 className="font-headline-sm text-headline-sm uppercase text-outline">Related topics</h2>
                </div>
                <span className="font-label-sm text-label-sm uppercase tracking-wider text-on-surface-variant">Connected by a shared Essential</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-space-md" data-testid="topic-related">
                {related.map((t) => (<TopicChip key={t.canonical_id} t={t} read={readSlugs.includes(t.slug)} />))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* GUIDES & RESOURCES — only when there is something to show */}
      {(availability.resources || availability.guides || availability.actions || availability.habits) && (
        <section className="w-full px-margin-mobile lg:px-margin py-space-2xl bg-surface-container-low">
          <div className="max-w-5xl mx-auto p-space-xl rounded-lg bg-surface shadow-sm" data-testid="topic-resources-status">
            <div className="flex items-start gap-space-md">
              <div className="w-10 h-10 rounded-full bg-primary-container text-surface-tint flex-shrink-0 flex items-center justify-center"><Icon name="menu_book" className="text-[22px]" /></div>
              <div className="flex flex-col">
                <h4 className="font-title-md text-title-md uppercase tracking-wider text-outline font-bold mb-space-xs">Guides, Evidence &amp; Actions</h4>
                <p className="font-body-md text-body-md text-on-surface-variant">Curated content is available for this topic below.</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="w-full px-margin-mobile lg:px-margin py-space-3xl bg-surface-container">
        <div className="max-w-4xl mx-auto text-center flex flex-col items-center">
          <span className="font-label-md text-label-md text-surface-tint uppercase tracking-widest font-bold mb-space-xs">KEEP EXPLORING</span>
          <h2 className="font-headline-lg text-headline-lg text-outline uppercase tracking-tight mb-space-md">Build a calm, personal practice</h2>
          <p className="font-body-lg text-body-lg text-on-surface-variant max-w-xl mb-space-2xl">Explore more of the framework, or open your personal Wellbeing space to focus on what matters most right now.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-space-md w-full sm:w-auto">
            <Link to="/resources" className="w-full sm:w-auto px-space-xl h-12 rounded bg-surface-tint text-on-primary font-label-md text-label-md uppercase tracking-widest font-bold shadow-[0_8px_24px_rgba(240,40,223,0.30)] hover:bg-primary transition-all flex items-center justify-center gap-space-xs">
              <Icon name="menu_book" className="text-[20px]" /> Back to the Library
            </Link>
            <Link to="/my-login" className="w-full sm:w-auto px-space-xl h-12 rounded bg-outline text-surface font-label-md text-label-md uppercase tracking-widest font-bold hover:bg-on-surface transition-all flex items-center justify-center gap-space-xs">
              <Icon name="person" className="text-[20px]" /> Open My Wellbeing Space
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
